import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// English setup with basic leave validation strings. Extend as needed.
const resources = {
  en: {
    translation: {
      admin: {
        leaveBalances: {
          title: 'Leave Balances',
          year: 'Year',
          refresh: 'Refresh',
          edit: 'Edit',
          typeCode: 'Type Code',
          columns: {
            employeeName: 'Employee',
            employeeEmail: 'Email',
            leaveTypeCode: 'Type Code',
            leaveTypeName: 'Type Name',
            entitledDays: 'Entitled',
            usedDays: 'Used',
            carriedOverDays: 'Carried Over',
            reservedDays: 'Reserved',
            availableNow: 'Available',
            actions: 'Actions'
          }
        }
      },
      dashboard: {
        leaveBalance: {
          title: 'Paid Leave Balance',
          ariaTable: 'Leave balance table',
          empty: 'No leave balance found.',
          error: 'Failed to load leave balance.',
          columns: {
            entitled: 'Entitled',
            used: 'Used',
            carried: 'Carried over',
            reserved: 'Reserved',
            available: 'Available'
          },
          legend: {
            available: 'Entitled + Carried over − Used − Reserved (available to request now)',
            entitled: 'Annual entitlement for the current year',
            used: 'Approved/consumed days to date',
            carried: 'Carried over from the previous year',
            reserved: 'Upcoming approved or pending requests (reserved)'
          }
        }
      },
      initialLeaveEntry: {
        title: 'Initial Leave Entry',
        add: 'Add',
        bulkSave: 'Bulk Save',
        reset: 'Reset',
        resetTitle: 'Reset Leave Data',
        resetPreview: 'This action will delete',
        cannotUndo: 'This action cannot be undone.',
        confirmReset: 'Confirm Reset',
        loading: 'Loading preview...',
        requests: 'requests',
        cycles: 'cycles',
        and: 'and',
        balances: 'balances',
        columns: {
          fullName: 'Full Name',
          leaveType: 'Leave Type',
          start: 'Start',
          end: 'End',
          businessDays: 'Business Days',
          status: 'Status',
          requestDate: 'Request Date',
          approvalDate: 'Approval Date',
          actions: 'Actions'
        }
      },
      common: {
        save: 'Save',
        saving: 'Saving...',
        cancel: 'Cancel',
        close: 'Close',
        add: 'Add',
        edit: 'Edit',
        view: 'View',
        loading: 'Loading...',
        selectAll: 'Select All',
      },
      careerEvents: {
        title: 'Career Events',
        addEvent: 'Add Event',
        noEvents: 'No career events recorded',
        selectEmployee: 'Select an employee to view career events',
        dialog: {
          addTitle: 'New Career Event',
          editTitle: 'Edit Event',
          viewTitle: 'View Event'
        },
        fields: {
          eventType: 'Event Type',
          date: 'Date',
          title: 'Title',
          description: 'Description',
          status: 'Status',
          outcome: 'Outcome'
        },
        status: {
          scheduled: 'Scheduled',
          inProgress: 'In Progress',
          completed: 'Completed',
          cancelled: 'Cancelled'
        },
        types: {
          REVIEW: 'Performance Review',
          PROMOTION: 'Promotion',
          SALARY: 'Salary Change',
          TRAINING: 'Training',
          CERT: 'Certification',
          PROJECT: 'Project Assignment',
          WARNING: 'Warning',
          GOAL: 'Goal'
        },
        errors: {
          requiredFields: 'Please fill in required fields',
          saveFailed: 'Failed to save'
        },
        followUp: {
          title: 'Follow-up Task',
          label: 'Follow-up',
          requiresFollowUp: 'Requires Follow-up',
          description: 'Follow-up Description',
          dueDate: 'Due Date',
          assignee: 'Assigned To',
          status: 'Status',
          completedAt: 'Completed',
          reminderFrequency: 'Reminder Frequency',
          noReminder: 'No reminder',
          daily: 'Daily',
          weekly: 'Weekly',
          biweekly: 'Every 2 weeks',
          monthly: 'Monthly',
          markComplete: 'Mark as Complete',
          addNew: 'Add Follow-up',
          nextDue: 'Next due',
          statusValues: {
            pending: 'Pending',
            completed: 'Completed',
            overdue: 'Overdue'
          }
        },
        deleteConfirm: {
          title: 'Delete Event',
          message: 'Are you sure you want to delete this event? This action cannot be undone.'
        }
      },
      dossier: {
        selectPerson: 'Select a person to view their dossier',
        noEmployee: 'No employee record. Create an employee record in the General tab first.',
        documentsPlaceholder: 'Additional document management features coming soon.',
        tabs: {
          general: 'General',
          careerEvents: 'Career Events',
          documents: 'Documents'
        },
        sections: {
          employee: 'Employee',
          contract: 'Contract',
          offer: 'Offer',
          overview: 'Overview Checklist',
          cvUpload: 'CV Upload & Processing'
        },
        backToList: 'Back to list'
      },
      leave: {
        validation: {
          validating: 'Validating request…',
          overlapTitle: 'Overlapping requests found',
          overlapHint: 'Your request overlaps with existing leave requests. Click to see details.',
          conflicts_one: '{{count}} conflict',
          conflicts_other: '{{count}} conflicts',
          toggleOverlapDetails: 'Toggle overlap details',
          cannotSubmit: 'Cannot submit request',
          warningsTitle: 'Please review',
          successProceed: 'Looks good! You can proceed.',
          errors: {
            INSUFFICIENT_BALANCE: 'Insufficient leave balance',
            OVERLAP: 'Overlapping request',
            PAST_DATE: 'Invalid date',
            INVALID_DAYS: 'Invalid number of days',
            TOO_MANY_DAYS: 'Too many days',
            EMPLOYEE_INACTIVE: 'Inactive employee',
            INVALID_LEAVE_TYPE: 'Invalid leave type',
            NO_BALANCE: 'No balance',
            VALIDATION_ERROR: 'Validation error',
            CANNOT_MODIFY: 'Cannot modify',
            REQUEST_NOT_FOUND: 'Request not found'
          },
          warnings: {
            SHORT_NOTICE: 'Short notice',
            TOO_CLOSE: 'Too close to another request',
            HIGH_USAGE: 'High usage of leave',
            LARGE_REQUEST: 'Large leave request'
          },
          messages: {
            INSUFFICIENT_BALANCE: 'You are requesting {{requested}} days, but only {{available}} days are available.',
            OVERLAP: 'The selected period overlaps another request. Please choose different dates.',
            PAST_DATE: 'You cannot request leave for past dates.',
            SHORT_NOTICE: 'We recommend submitting requests at least {{days}} days in advance.',
            TOO_CLOSE: 'Another request is too close to these dates. Keep at least 2 days gap.',
            HIGH_USAGE: 'This request will use {{used}} of your {{available}} available days. Remaining will be {{remaining}}.',
            LARGE_REQUEST: 'You are requesting {{days}} days at once. Consider splitting into smaller periods.',
            TOO_MANY_DAYS: 'The maximum number of consecutive business days is {{max}}.',
            INVALID_DAYS: 'The number of days must be greater than zero.',
            EMPLOYEE_INACTIVE: 'Your employee profile is inactive. Contact HR.',
            INVALID_LEAVE_TYPE: 'Selected leave type is not available. Please choose another.',
            NO_BALANCE: 'No leave balance is configured for {{year}}. Contact HR.',
            VALIDATION_ERROR: 'Validation failed. Please try again.'
          }
        }
      },
      notifications: {
        title: 'Notification Rules & Customized Email Templates',
        eventType: 'Event Type',
        rules: {
          title: 'Rules',
          audience: 'Audience',
          scopeDeptId: 'Scope Department Id',
          enabled: 'Enabled',
          add: 'Add Rule',
          scope: 'scope'
        },
        templates: {
          title: 'Email Templates',
          hideHelp: 'Hide',
          howItWorks: 'How it works',
          help: {
            selectEvent: 'Select Event Type above.',
            fill: 'Fill Subject and Body.',
            placeholders: 'Use placeholders in Body to auto-fill: {EmployeeName}, {StartDate}, {EndDate}, {BusinessDays}, {Reason}, {CancelReason}, {RequestId}, {ApproveUrl}, {RejectUrl}',
            html: 'Turn on HTML if your Body is HTML content.',
            whenSent: 'When it’s sent & recipients depend on the Event Type:',
            logicTitle: 'Who receives approver emails?',
            logic: {
              scope: 'Scope = requester\'s department; if none, global approvers.',
              managerEscalation: 'If requester is the department manager, scope escalates to the parent department.',
              fallback: 'If there are no approvers in the scoped department, fallback to global approvers.',
              multiple: 'If multiple approvers match the scope, all of them are notified (deduplicated by email).'
            },
            events: {
              newRequest: 'sent after a request is created; to approvers (scoped), includes Approve/Reject links.',
              newRequestConf: 'after creation; to the requester.',
              reminder: 'reminder for pending review; to approvers (scoped), with links.',
              cancelRequested: 'requester asks to cancel approved leave; to approvers (scoped), with Approve/Reject Cancel links.',
              cancelApproved: 'cancel approved; to requester (confirmation).',
              cancelRejected: 'cancel rejected; to requester (with approver comments if present).',
              cancelledBeforeApproval: 'pending leave cancelled by requester before approval; to approvers (visibility only).',
              cancelledByRequester: 'pending leave cancelled; to requester (confirmation).'
            }
          },
          subject: 'Subject',
          subjectHelp: 'Keep it short and action-oriented.',
          bodyText: 'Body (plain text)',
          html: 'HTML',
          preview: 'Preview',
          previewAria: 'Email preview',
          placeholdersTitle: 'Placeholders:',
          testTo: 'Test To (email)',
          sendTest: 'Send test',
          createCustom: 'Create Custom Template',
          update: 'Update Template',
          cancelEdit: 'Cancel',
          edit: 'Edit',
          add: 'Add Template'
        },
        defaults: {
          title: 'Default audiences by event (backend only, for reference)',
          audience: { requester: 'requester', approvers: 'approvers' },
          note: 'Defaults are applied only if there are no active rules for the event.'
        },
        test: {
          enterRecipient: 'Please enter a recipient email.',
          sent: 'Test email sent.',
          error: 'Failed to send test email.'
        }
      }
    }
  }
};


i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: 'en',
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
  });

export default i18n;

